import { ArrowRight, CalendarCheck, Phone } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { BOOKING_URL } from '../lib/blogToc'

/** Scrolls the (scrollable) index container so the active entry stays in view. */
export const revealActive = (list, activeId, behavior = 'smooth') => {
  if (!list || !activeId) return

  const target = Array.from(list.querySelectorAll('[data-toc-id]')).find(
    (node) => node.dataset.tocId === activeId
  )
  if (!target) return

  const box = list.getBoundingClientRect()
  const rect = target.getBoundingClientRect()
  if (rect.top < box.top + 8 || rect.bottom > box.bottom - 8) {
    list.scrollTo({
      top: list.scrollTop + (rect.top - box.top) - box.height / 3,
      behavior,
    })
  }
}

/**
 * Index of the article headings. h2 are the main entries; the h3 of a section
 * are only expanded while that section is the one being read.
 * `comfortable` enlarges the touch targets (used on mobile).
 */
export const TocList = ({ sections, activeId, onSelect, comfortable = false }) => {
  const parentSize = comfortable ? 'py-2.5 text-sm' : 'py-1.5 text-[13px]'
  const childSize = comfortable ? 'py-2 text-[13px]' : 'py-1 text-xs'

  const activeSectionId = sections.find(
    (section) =>
      section.id === activeId || section.children.some((child) => child.id === activeId)
  )?.id

  const handleClick = (event, id) => {
    event.preventDefault()
    onSelect(id)
  }

  return (
    <ol>
      {sections.map((section) => {
        const isOpen = section.id === activeSectionId

        return (
          <li key={section.id}>
            <a
              href={`#${section.id}`}
              data-toc-id={section.id}
              onClick={(event) => handleClick(event, section.id)}
              aria-current={section.id === activeId ? 'location' : undefined}
              className={`block border-l-2 pl-4 pr-2 leading-snug transition-colors ${parentSize} ${
                isOpen
                  ? 'border-orange-200 text-orange-200'
                  : 'border-neutral-800 text-neutral-400 hover:border-neutral-600 hover:text-neutral-100'
              }`}
            >
              <span className="line-clamp-2">{section.label}</span>
            </a>

            {section.children.length > 0 && (
              <div
                className={`grid transition-all duration-300 ease-out motion-reduce:transition-none ${
                  isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                }`}
              >
                <ul className="overflow-hidden" inert={!isOpen}>
                  {section.children.map((child) => (
                    <li key={child.id}>
                      <a
                        href={`#${child.id}`}
                        data-toc-id={child.id}
                        onClick={(event) => handleClick(event, child.id)}
                        aria-current={child.id === activeId ? 'location' : undefined}
                        className={`block border-l-2 pl-8 pr-2 leading-snug transition-colors ${childSize} ${
                          child.id === activeId
                            ? 'border-orange-200 text-orange-200'
                            : 'border-neutral-800 text-neutral-500 hover:border-neutral-600 hover:text-neutral-200'
                        }`}
                      >
                        <span className="line-clamp-2">{child.label}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </li>
        )
      })}
    </ol>
  )
}

export const BookingCard = () => (
  <div className="relative overflow-hidden rounded-xl border border-orange-200/20 bg-gradient-to-br from-neutral-900 via-neutral-900 to-neutral-950 p-6 text-center">
    <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-orange-200/10 blur-3xl" />
    <div className="pointer-events-none absolute -bottom-16 -left-16 h-40 w-40 rounded-full bg-orange-300/5 blur-3xl" />

    <div className="relative">
      <div className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-full border border-orange-200/20 bg-orange-200/10">
        <CalendarCheck className="h-5 w-5 text-orange-200" />
      </div>

      <p className="mb-2 text-[10px] uppercase tracking-[0.3em] text-orange-200/80">
        Miami · West Kendall
      </p>
      <h3 className="text-xl font-light leading-snug text-white">
        Book Your <span className="text-orange-200">Appointment</span>
      </h3>

      <div className="my-4 flex items-center justify-center gap-2">
        <div className="h-px w-8 bg-gradient-to-r from-transparent to-orange-200/50" />
        <div className="h-1 w-1 rounded-full bg-orange-200" />
        <div className="h-px w-8 bg-gradient-to-l from-transparent to-orange-200/50" />
      </div>

      <p className="mb-5 text-sm leading-relaxed text-neutral-400">
        Choose your provider, pick a time that works for you and reserve online.
      </p>

      <a
        href={BOOKING_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="group inline-flex w-full items-center justify-center gap-2 rounded bg-orange-200 px-6 py-3.5 text-xs font-semibold uppercase tracking-wider text-neutral-950 transition-colors hover:bg-orange-300"
      >
        Book Now
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </a>

      <a
        href="tel:+13053103160"
        className="mt-4 inline-flex items-center justify-center gap-2 text-xs text-neutral-500 transition-colors hover:text-orange-200"
      >
        <Phone className="h-3.5 w-3.5" />
        (305) 310-3160
      </a>
    </div>
  </div>
)

/** Desktop sidebar: sticky index with reading progress + booking call to action. */
const BlogSidebar = ({ sections, activeId, progress, onSelect }) => {
  const listRef = useRef(null)

  // Keep the active entry visible inside the (scrollable) index while reading.
  useEffect(() => {
    revealActive(listRef.current, activeId)
  }, [activeId])

  return (
    <aside
      aria-label="Article navigation"
      className="sticky top-28 w-72 shrink-0 self-start lg:hidden"
    >
      <div className="space-y-5">
        {sections.length > 0 && (
          <nav
            aria-label="In this article"
            className="overflow-hidden rounded-xl border border-neutral-800 bg-neutral-900/60 backdrop-blur-sm"
          >
            <div className="flex items-center justify-between px-5 py-3.5">
              <span className="flex items-center gap-2.5 text-[11px] uppercase tracking-[0.25em] text-neutral-200">
                <span className="h-1.5 w-1.5 rounded-full bg-orange-200" />
                In this article
              </span>
              <span className="text-[11px] tabular-nums text-neutral-500">{progress}%</span>
            </div>

            <div className="h-px w-full bg-neutral-800">
              <div
                className="h-full bg-gradient-to-r from-orange-200/60 to-orange-200 transition-[width] duration-300 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>

            <div
              ref={listRef}
              className="toc-scroll toc-fade max-h-[max(9rem,calc(100vh-35rem))] overflow-y-auto px-5 py-4"
            >
              <TocList sections={sections} activeId={activeId} onSelect={onSelect} />
            </div>
          </nav>
        )}

        <BookingCard />
      </div>
    </aside>
  )
}

export default BlogSidebar
